/**
 * Contrapartes.tsx — clientes y proveedores, con nombre.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ LA PANTALLA QUE FALTABA PARA QUE LA DECISIÓN DE LA 0080 FUERA HONESTA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El catálogo dejaba `customer` y `supplier` abiertas con un motivo escrito:
 * *«cerrarla obligaría a dar de alta un proveedor antes de poder registrar su
 * factura»*. La `0080` las cerró porque ese motivo se quedó viejo — y **esta
 * pantalla es la mitad que lo dejó viejo**. Sin ella, la migración habría
 * puesto un trámite imposible delante de registrar un gasto.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶▶ LO QUE ENSEÑA Y NO SE PODÍA VER ANTES
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * La lista trae el MOVIMIENTO de cada contraparte, no sólo su ficha: «¿a quién
 * le debo?» es la pregunta con la que alguien abre esto, y una lista de nombres
 * obligaría a ir a la antigüedad y volver.
 *
 * Y trae **el trabajo que falta**: una contraparte cuyo nombre es igual a su
 * código es una que la migración trajo de lo ya registrado y que nadie nombró.
 * Sale marcada. Sin esa marca, «Imprenta Nacional S.A.» e `IMPRENTA` se ven
 * igual de terminados en una tabla.
 */
import { useCallback, useEffect, useState } from 'react';
import { useIdioma } from '../nucleo/idioma.tsx';
import { puedeRegistrar, useSesion } from '../nucleo/sesion.tsx';
import { TIPOS_DE_IDENTIFICACION } from '../nucleo/identificacion.ts';
import {
  activarContraparte, agregarContraparte, editarContraparte, traerContrapartes,
  FalloDeApi, type Contraparte, type FichaDeContraparte,
} from '../datos/libros.ts';
import { SelectorDeCuenta } from '../componentes/SelectorDeCuenta.tsx';
import { SelectorDeMoneda } from '../componentes/SelectorDeMoneda.tsx';
import { useCuentasDeMovimiento } from '../nucleo/catalogo.tsx';
import type { Clave } from '../nucleo/idioma.ts';

const PAPELES = ['', 'cliente', 'proveedor'] as const;

/** Lo que el formulario lleva mientras se escribe. Todo texto: lo tecleado. */
interface Borrador {
  codigo: string;
  nombre: string;
  esCliente: boolean;
  esProveedor: boolean;
  tipoIdentificacion: string;
  correo: string;
  identificacion: string;
  diasDeCredito: string;
  moneda: string;
  cuentaDeCompra: string;
  cuentaDeVenta: string;
  nota: string;
}

const VACIO: Borrador = {
  codigo: '', nombre: '', esCliente: false, esProveedor: false,
  tipoIdentificacion: '', identificacion: '', correo: '', diasDeCredito: '', moneda: '',
  cuentaDeCompra: '', cuentaDeVenta: '', nota: '',
};

function deLaFicha(c: Contraparte): Borrador {
  return {
    codigo: c.codigo,
    nombre: c.nombre,
    esCliente: c.es_cliente,
    esProveedor: c.es_proveedor,
    tipoIdentificacion: c.tipo_identificacion ?? '',
    correo: c.correo ?? '',
    identificacion: c.identificacion_cruda ?? '',
    diasDeCredito: c.dias_de_credito === null ? '' : String(c.dias_de_credito),
    moneda: c.moneda ?? '',
    cuentaDeCompra: c.cuenta_de_compra ?? '',
    cuentaDeVenta: c.cuenta_de_venta ?? '',
    nota: c.nota ?? '',
  };
}

function aFicha(b: Borrador): FichaDeContraparte {
  const dias = b.diasDeCredito.trim();
  return {
    nombre: b.nombre.trim(),
    esCliente: b.esCliente,
    esProveedor: b.esProveedor,
    codigo: b.codigo.trim() === '' ? null : b.codigo.trim(),
    tipoIdentificacion: b.tipoIdentificacion === '' ? null : b.tipoIdentificacion,
    // Vacío entra como `null`: «nadie lo declaró», no una cadena vacía que
    // después hay que distinguir de un correo borrado.
    correo: b.correo.trim() === '' ? null : b.correo.trim(),
    identificacion: b.identificacion.trim() === '' ? null : b.identificacion.trim(),
    // ▶ Vacío es `null` y no cero. «Nadie declaró los días de crédito» y
    // «paga de contado» son dos hechos distintos, y un cero los junta.
    diasDeCredito: dias === '' ? null : Number(dias),
    moneda: b.moneda.trim() === '' ? null : b.moneda.trim().toUpperCase(),
    cuentaDeCompra: b.cuentaDeCompra.trim() === '' ? null : b.cuentaDeCompra.trim(),
    cuentaDeVenta: b.cuentaDeVenta.trim() === '' ? null : b.cuentaDeVenta.trim(),
    nota: b.nota.trim() === '' ? null : b.nota.trim(),
  };
}

export function Contrapartes() {
  const { t, monto } = useIdioma();
  const { pedir, sesion } = useSesion();
  const escribe = puedeRegistrar(sesion);

  const [lista, setLista] = useState<readonly Contraparte[] | null>(null);
  const [papel, setPapel] = useState('');
  const [texto, setTexto] = useState('');
  const [fallo, setFallo] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [editando, setEditando] = useState<string | null>(null);
  const [creando, setCreando] = useState(false);
  const [borrador, setBorrador] = useState<Borrador>(VACIO);

  /* ▶ En qué monedas se puede operar. Lo dice la base (`monedas_operables`,
     0094): la local siempre, y las que tienen tipo de cambio publicado. Una
     lista ISO acá ofrecería el euro, y una factura en euros sin tipo publicado
     no se puede registrar — el aviso llegaría el día de la factura. */
  /* ▶▶ SE FILTRA POR `tipo`, NO POR EL PRIMER DÍGITO.
     La primera versión de esto decía `codigo.startsWith('5') || startsWith('6')`
     y `Banco.tsx` ya tenía escrito por qué no: *cuál de ellas es el banco no lo
     dice el código — escribir `1102` acá sería la segunda copia del catálogo*.

     Y no era sólo doctrina: medido sobre el catálogo real, `tipo = 'expense'`
     son 33 cuentas y los prefijos 5 y 6 sólo 29. Las cuatro que faltaban son
     los gastos financieros y el impuesto de renta — y **el banco que cobra
     intereses también es un proveedor**. El filtro por prefijo le negaba su
     cuenta habitual. */
  const cuentasDeMovimiento = useCuentasDeMovimiento();
  const cuentasDeGasto = cuentasDeMovimiento.filter((c) => c.tipo === 'expense');
  const cuentasDeIngreso = cuentasDeMovimiento.filter((c) => c.tipo === 'income');

  const cargar = useCallback(async () => {
    setFallo(null);
    try {
      setLista(await traerContrapartes(pedir, papel || null, texto || null));
    } catch (e) {
      setLista(null);
      const f = e instanceof FalloDeApi ? e : null;
      setFallo(f?.clase === 'conexion' ? 'sb.conexion'
        : f?.codigo ? `sb.${f.codigo}` : 'sb.unknown');
    }
  }, [pedir, papel, texto]);

  useEffect(() => { void cargar(); }, [cargar]);

  async function correr(accion: () => Promise<void>) {
    setOcupado(true);
    setFallo(null);
    setAviso(null);
    try {
      await accion();
      await cargar();
    } catch (e) {
      const f = e instanceof FalloDeApi ? e : null;
      setFallo(f?.clase === 'conexion' ? 'sb.conexion'
        : f?.codigo ? `sb.${f.codigo}` : 'sb.unknown');
    } finally {
      setOcupado(false);
    }
  }

  const valido = borrador.nombre.trim() !== ''
    && (borrador.esCliente || borrador.esProveedor);

  function abrirAlta() {
    setCreando(true);
    setEditando(null);
    setBorrador(VACIO);
    setAviso(null);
  }

  function abrirEdicion(c: Contraparte) {
    setEditando(c.codigo);
    setCreando(false);
    setBorrador(deLaFicha(c));
    setAviso(null);
  }

  function cerrar() {
    setCreando(false);
    setEditando(null);
    setBorrador(VACIO);
  }

  const formulario = (
    <div className="stack" style={{ padding: '0 var(--s7) var(--s7)' }}>
      <div className="grid grid--form">
        <label className="field">
          <span className="field__label">{t('cp.name')}</span>
          <input
            className="input" value={borrador.nombre}
            placeholder={t('cp.nameHint')}
            onChange={(e) => setBorrador({ ...borrador, nombre: e.target.value })}
          />
        </label>
        {/* ▶ El código sólo se enseña al dar de alta, y vacío significa «que lo
            derive la base del nombre». Al editar no está: es el valor que viaja
            escrito dentro de cada movimiento suyo, y cambiarlo dejaría sus
            asientos apuntando a nadie. */}
        {creando ? (
          <label className="field">
            <span className="field__label">{t('cp.code')}</span>
            <input
              className="input input--mono" value={borrador.codigo}
              placeholder={t('cp.codeAuto')}
              onChange={(e) => setBorrador({ ...borrador, codigo: e.target.value })}
            />
            <span className="field__hint">{t('cp.codeWhy')}</span>
          </label>
        ) : (
          <label className="field">
            <span className="field__label">{t('cp.code')}</span>
            <input className="input input--mono" value={borrador.codigo} disabled readOnly />
            <span className="field__hint">{t('cp.codeLocked')}</span>
          </label>
        )}
      </div>

      <fieldset className="field" style={{ border: 0, padding: 0, marginTop: 'var(--s4)' }}>
        <legend className="field__label">{t('cp.role')}</legend>
        <label className="field field--inline">
          <input
            type="checkbox" checked={borrador.esCliente}
            onChange={(e) => setBorrador({ ...borrador, esCliente: e.target.checked })}
          />
          <span className="field__label">{t('cp.isCustomer')}</span>
        </label>
        <label className="field field--inline">
          <input
            type="checkbox" checked={borrador.esProveedor}
            onChange={(e) => setBorrador({ ...borrador, esProveedor: e.target.checked })}
          />
          <span className="field__label">{t('cp.isSupplier')}</span>
        </label>
        {/* Puede ser las dos cosas: se le compra y se le vende. Es frecuente, no
            un caso raro, y por eso son dos casillas y no una lista. */}
        <p className="field__hint">{t('cp.roleWhy')}</p>
      </fieldset>

      <div className="grid grid--form">
        <label className="field">
          <span className="field__label">{t('cp.idType')}</span>
          <select
            className="select" value={borrador.tipoIdentificacion}
            onChange={(e) => setBorrador({ ...borrador, tipoIdentificacion: e.target.value })}
          >
            <option value="">{t('cp.idNone')}</option>
            {TIPOS_DE_IDENTIFICACION.map((x) => (
              <option key={x} value={x}>{t(`tid.${x}` as Clave)}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field__label">{t('cp.id')}</span>
          <input
            className="input input--mono" value={borrador.identificacion}
            onChange={(e) => setBorrador({ ...borrador, identificacion: e.target.value })}
          />
          <span className="field__hint">{t('cp.idWhy')}</span>
        </label>
      </div>

      {/* ▶▶ NO HABÍA DÓNDE ANOTARLO. La ficha tenía todo lo que el LIBRO
          necesita —identificación, crédito, moneda, cuenta— y ningún dato de
          contacto, así que vivía en el correo de alguien o en una hoja aparte.

          ▶ `type="email"` es la misma forma que usan `/usuarios` y `/acceso`,
          y NO es la comprobación: el navegador ayuda, y quien decide es la base
          (`correo_de_contacto`, 0095, `SB183`). Un chequeo que sólo vive en la
          pantalla se rodea con una petición directa a la API. */}
      <label className="field">
        <span className="field__label">{t('cp.email')}</span>
        <input
          className="input" type="email" inputMode="email" value={borrador.correo}
          placeholder={t('cp.emailNone')}
          onChange={(e) => setBorrador({ ...borrador, correo: e.target.value })}
        />
        <span className="field__hint">{t('cp.emailWhy')}</span>
      </label>

      <div className="grid grid--form">
        <label className="field">
          <span className="field__label">{t('cp.credit')}</span>
          <input
            className="input input--mono" inputMode="numeric" value={borrador.diasDeCredito}
            placeholder={t('cp.creditNone')}
            onChange={(e) => setBorrador({ ...borrador, diasDeCredito: e.target.value })}
          />
          {/* Vacío no es cero: «nadie lo declaró» y «paga de contado» son dos
              hechos distintos, y un cero por omisión los junta. */}
          <span className="field__hint">{t('cp.creditWhy')}</span>
        </label>
        <label className="field">
          <span className="field__label">{t('cp.currency')}</span>
          {/* ▶▶ ERA UN CAMPO DE TEXTO DE TRES LETRAS.
              La única regla era la de la tabla —`~ '^[A-Z]{3}$'`—, que acepta
              `XYZ` y `EUR` exactamente igual: una no existe y la otra existe
              pero este sistema no tiene su tipo de cambio. Las dos producen un
              proveedor cuyas facturas no se pueden registrar, y eso se descubre
              el día de la primera factura. */}
          <SelectorDeMoneda
            valor={borrador.moneda} vacio={t('cp.currencyNone')}
            alCambiar={(m) => setBorrador({ ...borrador, moneda: m })}
          />
          <span className="field__hint">{t('cp.currencyWhy')}</span>
        </label>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          ▶▶ ESTE CAMPO FALTABA, Y ERA EL QUE SOSTENÍA LA PARAMETRIZACIÓN
          ══════════════════════════════════════════════════════════════════

          La `0081` decidió no construir una tabla de parámetros contables
          porque «la parametrización que se pidió es una columna que ya
          existía»: ésta. La base la guarda, la API la acepta, y la pantalla de
          cuentas por pagar la anuncia —«la habitual del proveedor»—.

          Y este formulario **no la ofrecía**, así que no había forma de
          escribirla desde ningún sitio. En la práctica cada factura tenía que
          traer su cuenta a mano, que es exactamente lo que la columna existe
          para evitar.

          Lo encontró la caminata del 12 de setiembre, dando de alta el primer
          proveedor de verdad. Ninguna prueba lo podía ver: las de base llaman a
          `agregar_contraparte` pasándole la cuenta, y ahí siempre funcionó.

          ▶ La forma es la de siempre acá: una promesa que no aterriza en ningún
          sitio visible no es una promesa, es la descripción de una. */}
      {/* ▶▶ SON DOS, Y CADA UNA SALE CON SU PAPEL.
          A quien se le compra impresión y se le vende software, una sola tendría
          que valer `5110` y `4120` a la vez. La `0080` ya había decidido esto
          para los papeles —dos casillas y no un `tipo`— y se le escapó para la
          cuenta; la `0084` la partió.

          ▶ Y se enseña la que corresponde: pedirle la cuenta de venta a alguien
          que sólo es proveedor es un campo que nunca se usa. */}
      {borrador.esProveedor && (
        <label className="field">
          <span className="field__label">{t('cp.account')}</span>
          {/* ▶▶ SE TECLEABA. Un `6210` escrito como `6120` entra sin quejarse
              —los dos existen— y sale a la luz en el estado de resultados de
              fin de mes, con las facturas de ese proveedor ya contra la cuenta
              equivocada. La columna existe para ahorrar trabajo; escrita a mano
              lo que ahorra es el aviso. */}
          <SelectorDeCuenta
            cuentas={cuentasDeGasto} valor={borrador.cuentaDeCompra}
            vacio={t('cp.accountNone')}
            alCambiar={(c) => setBorrador({ ...borrador, cuentaDeCompra: c })}
          />
          <span className="field__hint">{t('cp.accountWhy')}</span>
        </label>
      )}
      {borrador.esCliente && (
        <label className="field">
          <span className="field__label">{t('cp.accountSale')}</span>
          <SelectorDeCuenta
            cuentas={cuentasDeIngreso} valor={borrador.cuentaDeVenta}
            vacio={t('cp.accountNone')}
            alCambiar={(c) => setBorrador({ ...borrador, cuentaDeVenta: c })}
          />
          <span className="field__hint">{t('cp.accountSaleWhy')}</span>
        </label>
      )}

      <label className="field">
        <span className="field__label">{t('cp.note')}</span>
        <input
          className="input" value={borrador.nota}
          onChange={(e) => setBorrador({ ...borrador, nota: e.target.value })}
        />
      </label>

      <div className="page-actions" style={{ marginTop: 'var(--s5)' }}>
        <button
          className="btn btn--primary" type="button"
          disabled={ocupado || !valido}
          onClick={() => void correr(async () => {
            const ficha = aFicha(borrador);
            if (editando) {
              await editarContraparte(pedir, editando, ficha);
              setAviso(t('cp.saved', { nombre: ficha.nombre }));
            } else {
              // ▶ El código se pregunta a la BASE, que es quien lo deriva.
              // Calcularlo acá sería una segunda copia de esa regla, y el día
              // que la derivación cambie las dos dirían cosas distintas.
              const codigo = await agregarContraparte(pedir, ficha);
              setAviso(t('cp.created', { nombre: ficha.nombre, codigo }));
            }
            cerrar();
          })}
        >
          {editando ? t('cp.save') : t('cp.create')}
        </button>
        <button className="btn btn--ghost" type="button" onClick={cerrar}>
          {t('cat.cancel')}
        </button>
      </div>
    </div>
  );

  return (
    <div>
      <header className="page-head">
        <div>
          <h1>{t('cp.title')}</h1>
          <p className="small muted">{t('cp.sub')}</p>
        </div>
        <span className="spacer" />
        {escribe && !creando && !editando && (
          <button className="btn btn--secondary btn--sm" type="button" onClick={abrirAlta}>
            {t('cp.new')}
          </button>
        )}
      </header>

      {fallo && (
        <div className="alert alert--warn" style={{ marginBottom: 'var(--s6)' }}>
          <span>{t(fallo as Clave)}</span>
        </div>
      )}
      {aviso && (
        <div className="alert alert--ok" style={{ marginBottom: 'var(--s6)' }}>
          <span>{aviso}</span>
        </div>
      )}

      {(creando || editando) && (
        <section className="card" style={{ marginBottom: 'var(--s6)' }}>
          <div className="card__head">
            <h3>{editando ? t('cp.editing', { codigo: editando }) : t('cp.new')}</h3>
          </div>
          {formulario}
        </section>
      )}

      <section className="card filtros" style={{ marginBottom: 'var(--s6)' }}>
        <label className="field field--inline filtros__buscar">
          <span className="field__label">{t('cp.search')}</span>
          <input
            className="input" value={texto} placeholder={t('cp.searchHint')}
            onChange={(e) => setTexto(e.target.value)}
          />
        </label>
        <label className="field field--inline">
          <span className="field__label">{t('cp.role')}</span>
          <select
            className="select" value={papel}
            onChange={(e) => setPapel(e.target.value)}
          >
            {PAPELES.map((p) => (
              <option key={p} value={p}>{t(`cp.role.${p || 'all'}` as Clave)}</option>
            ))}
          </select>
        </label>
      </section>

      <section className="card card--pad0">
        <div className="card__head">
          <h3>{t('cp.list')}</h3>
          <span className="spacer" />
          <span className="small muted">
            {lista === null ? '' : t('cp.count', { n: String(lista.length) })}
          </span>
        </div>
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>{t('cp.name')}</th>
                <th>{t('cp.code')}</th>
                <th>{t('cp.role')}</th>
                <th>{t('cp.id')}</th>
                <th style={{ textAlign: 'right' }}>{t('cp.balance')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lista?.map((c) => (
                <tr key={c.codigo} className={c.activa ? undefined : 'muted'}>
                  <td>
                    {c.nombre}
                    {/* ▶ El trabajo que falta, a la vista. Una contraparte cuyo
                        nombre es su código la trajo la migración de lo ya
                        registrado y nadie la ha nombrado — y en una tabla eso
                        se ve igual de terminado que las demás. */}
                    {c.sin_nombrar && (
                      <> <span className="chip chip--warn">{t('cp.unnamed')}</span></>
                    )}
                    {!c.activa && (
                      <> <span className="chip">{t('cp.inactive')}</span></>
                    )}
                    {/* ▶ El correo va DEBAJO del nombre y no en columna propia.
                        La tabla ya tiene cinco y en un teléfono una sexta la
                        manda al scroll horizontal; y el correo se lee junto al
                        nombre —«a quién y a dónde»—, no comparado hacia abajo
                        contra los correos de los demás, que es lo que una
                        columna invita a hacer. */}
                    {c.correo && <span className="cp__correo">{c.correo}</span>}
                  </td>
                  <td className="mono small">{c.codigo}</td>
                  <td className="small">
                    {c.es_cliente && <span className="chip chip--acc">{t('cp.customer')}</span>}
                    {c.es_cliente && c.es_proveedor && ' '}
                    {c.es_proveedor && <span className="chip">{t('cp.supplier')}</span>}
                  </td>
                  <td className="mono small">{c.identificacion ?? '—'}</td>
                  <td className="mono small" style={{ textAlign: 'right' }}>
                    {/* El saldo sale del LIBRO y viaja como texto: acá no se
                        guarda ningún total. Un total guardado sería la segunda
                        versión de la misma verdad. */}
                    {/* ▶ UN SALDO A FAVOR SE DICE, no se enseña con un menos.
                        Y lo dice la BASE, en `a_favor`, no esta pantalla: la
                        primera versión comparaba el saldo contra cero acá
                        mismo, convirtiéndolo, y la cazó el guardián 3 — el
                        dinero viaja como texto y no toca la aritmética del
                        navegador. El arreglo salió mejor que el atajo que
                        impidió: que un cliente esté a favor es una lectura
                        contable, y decidirla de este lado la pone donde nadie
                        la va a encontrar.

                        ▶ Y el guardián la encontró DENTRO de este comentario
                        la primera vez que se escribió, porque el quitador de
                        comentarios trabaja por línea y un bloque de JSX
                        conserva las de en medio. Está dicho en el módulo de
                        alcanzabilidad como límite conocido, y acá se cobró
                        solo: la prosa se mide como si fuera código.
                        Un cliente puede haber pagado más de lo facturado —un
                        anticipo— y eso es un estado legítimo, no un error. Pero
                        «−₡6 397 320» en una columna que se lee como «lo que me
                        debe» se lee como un defecto: quien lo ve va a buscar
                        qué se rompió. Se enseña el importe y se nombra el
                        lado. Es la misma decisión que la antigüedad tomó con
                        su columna aparte. */}
                    {c.movimientos === 0
                      ? <span className="muted">—</span>
                      : (
                        <>
                          {monto({
                            valor: c.saldo,
                            moneda: sesion?.empresa.monedaLocal ?? '',
                          })}
                          {c.a_favor && (
                            <> <span className="chip chip--warn">{t('cp.inFavour')}</span></>
                          )}
                        </>
                      )}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {escribe && (
                      <>
                        <button
                          className="btn btn--ghost btn--sm" type="button"
                          disabled={ocupado}
                          onClick={() => abrirEdicion(c)}
                        >
                          {t('cp.edit')}
                        </button>
                        {' '}
                        {/* ▶ No hay borrar, y no es un olvido: el código de una
                            contraparte está escrito DENTRO de la clasificación
                            de cada movimiento suyo. Desactivarla deja de
                            ofrecerla para registrar y conserva su historia —
                            dejar de comprarle a alguien no borra lo que se le
                            debe. */}
                        <button
                          className="btn btn--ghost btn--sm" type="button"
                          disabled={ocupado}
                          onClick={() => void correr(async () => {
                            await activarContraparte(pedir, c.codigo, !c.activa);
                          })}
                        >
                          {c.activa ? t('cp.deactivate') : t('cp.activate')}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {lista !== null && lista.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted" style={{ padding: 'var(--s7)' }}>
                    {texto || papel ? t('cp.noneFound') : t('cp.none')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
